import { useCallback, useEffect, useState } from 'react';
import { ChatMessage } from '../models/chat.model.ts';
import { useSelector } from "react-redux";
import { IRootReducerState } from "@/store/reducers";
import commonService from '@/services/common.service';

interface ExtraMetadata {
    model_usage?: {
        input_tokens: number;
        output_tokens: number;
        total_tokens: number;
    };
    session_id?: string;
    s3_key?: string;
    files?: {
        [key: string]: {
            line_count: number;
            file_hash: string;
        };
    };
    db_query?: any;
    db_query_result?: any;
    db_query_title?: string;
    imagePreviewUrl?: string; // This will hold the URL for rendering
    image_public_url?: string; // This is the key from your API
    scratchPad?: string; // Streaming scratch pad content
    scratchPadActive?: boolean; // Whether scratch pad streaming is active
    scratchPadDone?: boolean; // Whether scratch pad streaming finished
    processItems?: import('@/components/common-components/advanced-mode-process-loader/AdvancedModeProcessLoaderComponent').ProcessItem[]; // Array of process items (messages and tool calls) for advanced mode
    isAdvancedMode?: boolean; // Whether this message is from advanced mode
    is_streaming?: boolean; // Whether the message is currently streaming
}

interface ExtendedChatMessage extends ChatMessage {
    extra?: ExtraMetadata;
}

export const useChatState = () => {
    const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
    const [isChatInProgress, setIsChatInProgress] = useState<boolean>(false);
    const currentChatHistories = useSelector((state: IRootReducerState) => state.projectData.currentChatHistories);

    const addUserMessage = useCallback((input: string, image?: File | null) => {
        const userMessage: ExtendedChatMessage = {
            id: Date.now() + Math.floor(Math.random() * 1000000000),
            role: 'user',
            message: input || (image ? '' : "Hi"),
            extra: {
                imagePreviewUrl: image ? URL.createObjectURL(image) : undefined,
            }
        };
        //check if there is a loading message, if yes, update it with the user message

        setMessages(prevMessages => {
            const tempMessages = [...prevMessages];
            if (tempMessages?.length > 0 && tempMessages[tempMessages.length - 1].role === 'loading') {
                tempMessages[tempMessages.length - 1] = {
                    ...tempMessages[tempMessages.length - 1],
                    role: 'agent',
                }
            }
            // Return tempMessages with the modified loading->agent message plus the new user message
            return [...tempMessages, userMessage];
        });
    }, []);

    const addLoadingMessage = useCallback(() => {
        const loadingMessage: ExtendedChatMessage = {
            id: Date.now() + Math.floor(Math.random() * 1000000000),
            role: 'loading',
            message: ''
        };
        setMessages(prevMessages => [...prevMessages, loadingMessage]);
        return loadingMessage;
    }, []);

    const updateLoadingMessage = useCallback((message: ExtendedChatMessage | Partial<ExtendedChatMessage>, isStreaming?: boolean) => {
        setMessages((prevMessages) => {
            let lastMessage;
            if (prevMessages && prevMessages.length > 0) {
                lastMessage = prevMessages[prevMessages?.length - 1]
            }
            if (lastMessage && lastMessage.role === 'loading') {
                if (message.message || message.extra?.scratchPad) {
                    const newMessage = {
                        ...lastMessage,
                        message: isStreaming ? (lastMessage.message + (message.message || '')) : (message.message || ''),
                        extra: {
                            ...lastMessage.extra,
                            ...message.extra,
                            scratchPad: isStreaming ? ((lastMessage.extra?.scratchPad || '') + (message.extra?.scratchPad || '')) : message.extra?.scratchPad || ''
                        }
                    };
                    const newMessages = [...prevMessages];
                    newMessages[newMessages.length - 1] = newMessage;
                    return newMessages;
                }

                // For file updates, merge the file information
                if (message.extra?.files) {
                    const newMessage = {
                        ...lastMessage,
                        extra: {
                            ...lastMessage.extra,
                            ...message.extra,
                            files: {
                                ...lastMessage.extra?.files,
                                ...message.extra.files
                            }
                        }
                    };
                    const newMessages = [...prevMessages];
                    newMessages[newMessages.length - 1] = newMessage;
                    return newMessages;
                }

                // For processItems updates only (advanced mode), just update the extra field
                if (message.extra?.processItems !== undefined) {
                    const newMessage = {
                        ...lastMessage,
                        extra: {
                            ...lastMessage.extra,
                            ...message.extra,
                            processItems: message.extra.processItems,
                            isAdvancedMode: message.extra.isAdvancedMode !== undefined ? message.extra.isAdvancedMode : lastMessage.extra?.isAdvancedMode
                        }
                    };
                    const newMessages = [...prevMessages];
                    newMessages[newMessages.length - 1] = newMessage;
                    return newMessages;
                }

                // For other updates, merge the content
                const newMessage = {
                    ...lastMessage,
                    ...message,
                    extra: {
                        ...lastMessage.extra,
                        ...message.extra
                    }
                };
                const newMessages = [...prevMessages];
                newMessages[newMessages.length - 1] = newMessage;
                return newMessages;
            }
            else if (message && 'message' in message) {
                //add a loading message
                const loadingMessage: ExtendedChatMessage = {
                    id: Date.now() + Math.floor(Math.random() * 1000000000),
                    role: 'loading',
                    message: message.message || '',
                    extra: {
                        ...message.extra
                    }
                };
                return [...prevMessages, loadingMessage];
            }
            return prevMessages; // Return previous state if no loading message found
        });
    }, []);

    const handleErrorMessage = useCallback((errorMessage?: string) => {
        if (errorMessage !== "Insufficient budget to start session" && !errorMessage?.includes("Failed to start Claude Code")) {
            commonService._communications.aiConcurrencyErrorSubject.next({
                error: errorMessage || "Unable to connect to the AI agent now. Please try again later.",
                timestamp: new Date().toISOString()
            });
        }
        else if (errorMessage?.includes("Failed to start Claude Code")) {
            // Trigger advanced mode error dialog to suggest switching to normal mode
            commonService._communications.advancedModeErrorSubject.next({
                error: errorMessage || "Advanced mode is currently unavailable. Please switch to normal mode.",
                timestamp: new Date().toISOString()
            });
        }
        setMessages(prevMessages => {
            const loadingIndex = prevMessages.findIndex(msg => msg.role === 'loading');
            if (loadingIndex !== -1) {
                // Found a loading message, replace it
                const newMessages = [...prevMessages];
                newMessages[loadingIndex] = {
                    ...newMessages[loadingIndex],
                    role: 'agent',
                    message: errorMessage ?? "Unable to connect to the AI agent now. Please try again later.",
                    type: "error"
                };
                return newMessages;
            } else {
                // No loading message, add a new error message
                const newErrorMessage: ExtendedChatMessage = {
                    id: Date.now() + Math.floor(Math.random() * 1000000000),
                    role: 'agent',
                    message: errorMessage ?? "An error occurred.",
                    type: "error",
                };
                return [...prevMessages, newErrorMessage];
            }
        });
        setIsChatInProgress(false);
    }, []);

    const setInitialMessages = useCallback((apiMessages: ExtendedChatMessage[]) => {
        setMessages(apiMessages);
    }, []);

    const onApplySuccess = useCallback((messageId: number) => {
        setMessages(prevMessages => {
            return prevMessages.map(msg =>
                msg.id === messageId
                    ? { ...msg, is_migrate_success: true }
                    : msg
            );
        });
    }, []);

    //handle retry message by removing last ai message and adding a loading message
    const handleRetryMessage = useCallback(() => {
        setMessages(prevMessages => {
            const newMessages = [...prevMessages];
            newMessages.pop();
            return newMessages;
        });
    }, []);


    useEffect(() => {
        if (currentChatHistories?.length > 0) {
            // Transform the incoming chat history to map image_public_url to imagePreviewUrl
            const formattedMessages = currentChatHistories.map((msg: any) => ({
                ...msg,
                extra: {
                    ...msg.extra,
                    imagePreviewUrl: msg.extra?.image_public_url || msg.extra?.imagePreviewUrl || undefined
                }
            }));
            setInitialMessages(formattedMessages);
        }
    }, [currentChatHistories, setInitialMessages]);


    return {
        messages,
        isChatInProgress,
        setIsChatInProgress,
        addUserMessage,
        addLoadingMessage,
        updateLoadingMessage,
        handleErrorMessage,
        setInitialMessages,
        onApplySuccess,
        handleRetryMessage
    };
};